// stale-chunk-handler.ts — Catches dynamic import (preload) failures
//
// After a deploy, the old index.html references chunk hashes that no longer
// exist on the server. The only fix is a page reload to pick up the new
// index.html. We allow ONE reload — if chunks still fail after that, it's
// not a stale chunk problem and reloading again won't help (and would cause
// an infinite reload loop that hammers Keycloak).

import * as Sentry from "@sentry/react";

declare const __APP_VERSION__: string;
declare const __COMMIT_SHA__: string;

const appVersion =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown";
const commitSha =
  typeof __COMMIT_SHA__ !== "undefined" ? __COMMIT_SHA__ : "unknown";

// Use commit SHA for reload tracking — version alone doesn't change between
// same-version redeploys, which causes the handler to think it already
// reloaded when it hasn't fetched the new chunks yet.
const buildId = `${appVersion}-${commitSha}`;

const RELOAD_KEY = "stale_chunk_reload";

window.addEventListener("vite:preloadError", (event: Event) => {
  const e = event as CustomEvent;
  event.preventDefault();

  const hasReloaded = sessionStorage.getItem(RELOAD_KEY) === buildId;

  Sentry.withScope((scope) => {
    scope.setTag("error_type", "stale_chunk");
    scope.setTag("app_version", appVersion);
    scope.setTag("already_reloaded", String(hasReloaded));
    scope.setLevel("warning");
    scope.setContext("chunk_error", {
      message: e.detail?.message || "Dynamic import failed",
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });

    Sentry.captureException(
      new Error(`Chunk preload failure (v${appVersion})`)
    );
  });

  if (!hasReloaded) {
    // First failure for this version — reload to get new index.html
    sessionStorage.setItem(RELOAD_KEY, buildId);
    Sentry.flush(2000).finally(() => {
      window.location.reload();
    });
  }
  // Already reloaded once for this version — don't loop.
  // Error falls through to WidgetErrorBoundary.
});
